/**
 * Translating between local records and the sync contract.
 *
 * The local model and the wire model are deliberately not the same shape: the
 * local one carries a monotonic `revision` that only this device increments,
 * while the wire one carries the *remote* revision a change was based on. Keeping
 * them apart is what makes conflict detection meaningful — if the two were one
 * field, "I changed this" and "the server changed this" would be
 * indistinguishable.
 *
 * This module only reads and writes. Every decision about *whether* to write —
 * fast-forward, bind, or preserve both sides — belongs to the engine, so that
 * the rule is in one readable place instead of spread across four kinds.
 */

import type { Repositories } from '../storage/repositories';
import type { ProgressBackend } from '../curriculum/progress';
import { bumped, freshMeta, type StoredDocument } from '../storage/types';
import type { LessonProgress } from '../curriculum/types';
import type { PulledRecord, SyncKind } from './types';

export interface LocalContext {
  repos: Repositories;
  progress: ProgressBackend;
}

/**
 * Settings the device keeps to itself.
 *
 * The adoption ledger records which of *this* iPad's records an account has
 * already been offered. Syncing it would tell a second device that its own
 * unrelated local work had already been dealt with, so the learner would never
 * be asked and that work would never sync. The server refuses these keys too;
 * this is the near half of the same fence.
 */
export const LOCAL_ONLY_SETTINGS = new Set(['account-adoption', 'sync-state']);

/** Deep equality via a key-stable serialisation. */
export function payloadsEqual(a: unknown, b: unknown): boolean {
  return stable(a) === stable(b);
}

function stable(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`).join(',')}}`;
}

/** What the local side holds for one record. */
export interface LocalRecord {
  payload: Record<string, unknown>;
  title: string;
  feature: string;
  /** Monotonic, incremented by this device only. Absent for kinds without one. */
  revision: number;
  deleted: boolean;
}

export async function readLocalRecord(
  ctx: LocalContext,
  kind: SyncKind,
  recordId: string,
): Promise<LocalRecord | null> {
  switch (kind) {
    case 'document': {
      const row = await ctx.repos.documents.get(recordId);
      if (!row) return null;
      return {
        payload: row.payload as unknown as Record<string, unknown>,
        title: row.title,
        feature: row.feature,
        revision: row.revision,
        deleted: row.deletedAt !== null,
      };
    }
    case 'lesson_progress': {
      const row = (await ctx.progress.load())[recordId];
      if (!row) return null;
      // Lesson progress has no local revision of its own; it is merged rather
      // than conflicted (see `mergeProgress`), so none is needed.
      return {
        payload: row as unknown as Record<string, unknown>,
        title: '',
        feature: '',
        revision: 0,
        deleted: false,
      };
    }
    case 'user_settings': {
      const row = await ctx.repos.settings.get(recordId);
      if (!row) return null;
      return { payload: row.value, title: '', feature: '', revision: row.revision, deleted: false };
    }
    case 'practice_session': {
      const rows = await ctx.repos.sessions.list(500);
      const row = rows.find((candidate) => candidate.id === recordId);
      if (!row) return null;
      return {
        payload: {
          feature: row.feature,
          lessonId: row.lessonId,
          result: row.result,
          startedAt: row.startedAt,
          completedAt: row.completedAt,
        },
        title: '',
        feature: row.feature,
        revision: row.revision,
        deleted: false,
      };
    }
  }
}

/**
 * Write the server's version over the local one, and report the new local revision.
 *
 * Only called when the engine has established that this device has nothing
 * unsent for the record — see `SyncEngine.pull`. It is never a way to resolve a
 * disagreement.
 */
export async function writeRemote(ctx: LocalContext, record: PulledRecord): Promise<number> {
  switch (record.kind) {
    case 'document':
      return writeDocument(ctx, record);
    case 'lesson_progress':
      return writeProgress(ctx, record);
    case 'user_settings':
      return writeSetting(ctx, record);
    case 'practice_session':
      return writeSession(ctx, record);
  }
}

async function writeDocument(ctx: LocalContext, record: PulledRecord): Promise<number> {
  const existing = await ctx.repos.documents.get(record.record_id);
  const updatedAt = Date.parse(record.updated_at) || Date.now();

  if (record.deleted) {
    // A tombstone from elsewhere. Removing an absent or already-removed record
    // is a no-op, which is what makes replaying a page harmless.
    if (existing && existing.deletedAt === null) await ctx.repos.documents.remove(record.record_id);
    const after = await ctx.repos.documents.get(record.record_id);
    return after?.revision ?? 1;
  }

  const payload = record.payload as unknown as StoredDocument['payload'];
  if (!existing) {
    const created: StoredDocument = {
      ...freshMeta(updatedAt),
      id: record.record_id,
      title: record.title,
      feature: record.feature,
      schemaVersion: record.schema_version,
      payload,
    };
    await ctx.repos.documents.put(created);
    return created.revision;
  }

  const next = {
    ...bumped(existing, updatedAt),
    title: record.title,
    feature: record.feature || existing.feature,
    schemaVersion: record.schema_version,
    payload,
  };
  await ctx.repos.documents.put(next);
  return next.revision;
}

/**
 * Lesson progress merges instead of conflicting — but not field by field.
 *
 * The fields fall into two groups with genuinely different semantics, and
 * treating them alike produces a record that never happened.
 *
 * **Achievements** are monotonic. A lesson once passed stays passed; a best
 * streak is a high-water mark. Taking the better of each side loses nothing.
 *
 * **The scheduling bundle** — `earStreak`, `reviewCount`, `dueAt` — is not.
 * `earStreak` is deliberately reset to zero by a wrong answer, so taking the
 * maximum resurrects a streak the learner has actually lost, and pairs it with a
 * review schedule from the other side that never coexisted with it. These three
 * describe one moment of practice, so they travel together from whichever side
 * practised last and are never interleaved.
 *
 * The tie-breaker, when both sides carry the same `lastPractisedAt`: the higher
 * `reviewCount` wins, and if that is equal too, the remote side wins. It has to
 * be deterministic in this direction specifically — every device merges against
 * the same server record, so preferring the remote on a true tie is what makes
 * two devices converge on one answer instead of overwriting each other forever.
 *
 * This is emphatically not what happens to handwriting. Strokes have no "better"
 * version and no ordering, so documents take the conflict path and ask.
 */
export function mergeProgress(local: LessonProgress, remote: LessonProgress): LessonProgress {
  const schedule = laterPractice(local, remote);
  return {
    lessonId: local.lessonId || remote.lessonId,

    // Achievements: high-water marks.
    inkPassed: local.inkPassed || remote.inkPassed,
    earBest: Math.max(local.earBest, remote.earBest),
    creativeDone: local.creativeDone || remote.creativeDone,

    // One practice moment, taken whole.
    earStreak: schedule.earStreak,
    reviewCount: schedule.reviewCount,
    dueAt: schedule.dueAt,

    // The last time either side practised is a fact about both.
    lastPractisedAt: maxOrNull(local.lastPractisedAt, remote.lastPractisedAt),
  };
}

/** Whichever side practised most recently, by the documented tie-breaker. */
function laterPractice(local: LessonProgress, remote: LessonProgress): LessonProgress {
  const localAt = local.lastPractisedAt;
  const remoteAt = remote.lastPractisedAt;

  // A side that has never been practised cannot own the schedule.
  if (localAt === null && remoteAt === null) return remote;
  if (localAt === null) return remote;
  if (remoteAt === null) return local;

  if (localAt !== remoteAt) return localAt > remoteAt ? local : remote;
  if (local.reviewCount !== remote.reviewCount) {
    return local.reviewCount > remote.reviewCount ? local : remote;
  }
  return remote;
}

function maxOrNull(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.max(a, b);
}

async function writeProgress(ctx: LocalContext, record: PulledRecord): Promise<number> {
  const incoming = { ...(record.payload as unknown as LessonProgress), lessonId: record.record_id };
  const existing = (await ctx.progress.load())[record.record_id];
  await ctx.progress.save(existing ? mergeProgress(existing, incoming) : incoming);
  return 0;
}

async function writeSetting(ctx: LocalContext, record: PulledRecord): Promise<number> {
  if (LOCAL_ONLY_SETTINGS.has(record.record_id)) return 0;
  const row = await ctx.repos.settings.set(record.record_id, record.payload);
  return row.revision;
}

async function writeSession(ctx: LocalContext, record: PulledRecord): Promise<number> {
  // Append-only: a session already held locally is a fact that does not change.
  const rows = await ctx.repos.sessions.list(500);
  const existing = rows.find((row) => row.id === record.record_id);
  if (existing) return existing.revision;

  const payload = record.payload as {
    feature?: string;
    lessonId?: string | null;
    result?: Record<string, unknown>;
    startedAt?: number;
    completedAt?: number | null;
  };
  const created = await ctx.repos.sessions.append({
    ...freshMeta(Date.parse(record.updated_at) || Date.now()),
    id: record.record_id,
    feature: String(payload.feature ?? record.feature ?? ''),
    lessonId: payload.lessonId ?? null,
    result: payload.result ?? {},
    startedAt: payload.startedAt ?? Date.now(),
    completedAt: payload.completedAt ?? null,
  });
  return created.revision;
}
